import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Card,
  Chip,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import api from "../../src/utils/api";
import EditPropertyModal from "../components/EditPropertyModal";

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/properties`);
      setProperties(response.data || []);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
      setSnackbar({
        open: true,
        message: "Failed to load properties.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleView = (account_id) => {
    navigate(`/home/clubmembers/${account_id}?from=properties`);
  };

  const handleEdit = (property) => {
    setSelectedPropertyId(property.id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPropertyId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;

    try {
      await api.delete(`/properties/${id}`);
      setProperties((prev) => prev.filter((prop) => prop.id !== id));
      setSnackbar({
        open: true,
        message: "Property deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete property:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete property.",
        severity: "error",
      });
    }
  };

  const filteredProperties = useMemo(() => {
    return properties.filter((property) =>
      [
        property.account_id,
        property.street_1,
        property.city,
        property.state,
        property.postal_code,
        property.mailing_name,
        property.emails,
        property.phone_numbers,
        property.neighborhood,
      ]
        .map((val) => String(val || "").toLowerCase())
        .some((val) => val.includes(searchQuery))
    );
  }, [properties, searchQuery]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionHeaderIconSx = {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const columns = [
    {
      field: "account_id",
      headerName: "Club Member Code",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "street_1",
      headerName: "Street",
      flex: 1.5,
      minWidth: 190,
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "state",
      headerName: "State",
      flex: 0.6,
      minWidth: 90,
    },
    {
      field: "postal_code",
      headerName: "ZIP",
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: "mailing_name",
      headerName: "Mailing Name",
      flex: 1.3,
      minWidth: 180,
    },
    {
      field: "emails",
      headerName: "Email",
      flex: 1.4,
      minWidth: 220,
    },
    {
      field: "phone_numbers",
      headerName: "Phone",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "neighborhood",
      headerName: "Neighborhood",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.9,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", py: 0.5 }}>
          <Tooltip title="View Member">
            <IconButton
              onClick={() => handleView(params.row.account_id)}
              size="small"
              sx={{
                border: "1px solid rgba(47,99,173,0.12)",
                borderRadius: "10px",
                backgroundColor: "rgba(47,99,173,0.03)",
                color: "#2f63ad",
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Property">
            <IconButton
              onClick={() => handleEdit(params.row)}
              size="small"
              sx={{
                border: "1px solid rgba(245,158,11,0.16)",
                borderRadius: "10px",
                backgroundColor: "rgba(245,158,11,0.08)",
                color: "#d97706",
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Property">
            <IconButton
              onClick={() => handleDelete(params.row.id)}
              size="small"
              sx={{
                border: "1px solid rgba(220,38,38,0.12)",
                borderRadius: "10px",
                backgroundColor: "rgba(220,38,38,0.04)",
                color: "#dc2626",
                "&:hover": {
                  backgroundColor: "rgba(220,38,38,0.1)",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2.5,
            pb: 1.75,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
            <Box sx={sectionHeaderIconSx}>
              <HomeWorkIcon sx={{ color: "#2f63ad" }} />
            </Box>

            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
              >
                Properties
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.25 }}>
                Search, review, edit, and manage property records.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <TextField
              fullWidth
              label="Search properties"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearch}
              sx={{
                maxWidth: 420,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "rgba(255,255,255,0.96)",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#6b778c" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Chip
              label={`Total Records: ${filteredProperties.length}`}
              variant="outlined"
              sx={{
                alignSelf: { xs: "flex-start", md: "center" },
                borderRadius: "999px",
                fontWeight: 700,
                borderColor: "rgba(47,99,173,0.15)",
                backgroundColor: "rgba(47,99,173,0.04)",
                color: "#223142",
              }}
            />
          </Box>

          {loading ? (
            <Box
              sx={{
                minHeight: "320px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : filteredProperties.length === 0 ? (
            <Card
              sx={{
                borderRadius: "16px",
                border: "1px dashed rgba(47,99,173,0.18)",
                boxShadow: "none",
                p: 4,
                textAlign: "center",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                No properties found
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                Try adjusting your search to find matching property records.
              </Typography>
            </Card>
          ) : (
            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                borderRadius: "16px",
                border: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "#fff",
              }}
            >
              <DataGrid
                rows={filteredProperties}
                columns={columns}
                getRowId={(row) => row.id}
                autoHeight
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25, page: 0 },
                  },
                }}
                disableRowSelectionOnClick
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "rgba(47,99,173,0.04)",
                    color: "#223142",
                    fontWeight: 700,
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                  },
                  "& .MuiDataGrid-cell": {
                    borderColor: "rgba(47,99,173,0.06)",
                    color: "#223142",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-row": {
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "rgba(47,99,173,0.03)",
                    },
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid rgba(47,99,173,0.08)",
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Card>

      {modalOpen && (
        <EditPropertyModal
          open={modalOpen}
          handleClose={handleCloseModal}
          propertyId={selectedPropertyId}
          onSave={fetchProperties}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertiesPage;